import { ArrowSquareOutIcon } from '@phosphor-icons/react'
import { typography } from '@pixee/design-system'
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { match, P } from 'ts-pattern'
import { isInteractiveElementClick } from '../../components/table/row-interaction'
import { Table } from '../../components/table/table.tsx'
import type { RefreshRequestResponse, RefreshTriggerSourceResponse } from '../../utils/api-client/api-schemas.ts'
import { formatRelativeTime } from '../../utils/date-utils.ts'
import * as styles from './refresh-request-table.css.ts'
import { RefreshStateBadge } from './refresh-state-badge.tsx'

const getTriggerLabel = (trigger: RefreshTriggerSourceResponse, pullRequestNumber?: number): string =>
  match(trigger)
    .with('onboarding', () => 'Repository added')
    .with('direct-push', () => 'Push to default branch')
    .with('merged-pr', () => (pullRequestNumber ? `PR #${pullRequestNumber} merged` : 'PR merged'))
    .exhaustive()

const columnHelper = createColumnHelper<RefreshRequestResponse>()

const columns = [
  columnHelper.accessor('state', {
    header: 'Status',
    cell: ({ row }) => <RefreshStateBadge state={row.original.state} triggerSource={row.original.trigger_source} />,
    meta: { width: '160px' },
  }),
  columnHelper.accessor('trigger_source', {
    header: 'Trigger',
    cell: ({ row }) => getTriggerLabel(row.original.trigger_source, row.original.pull_request_number),
    meta: { width: '30%', className: typography.paragraph },
  }),
  columnHelper.display({
    id: 'commit',
    header: 'Commit',
    cell: ({ row }) => (
      <CommitCell shortSha={row.original.after_sha?.slice(0, 7)} commitLink={row.original._links.commit?.href} />
    ),
    meta: { width: '20%', className: typography.paragraph },
  }),
  columnHelper.display({
    id: 'pull-request',
    header: 'Pull request',
    cell: ({ row }) => (
      <PullRequestCell
        triggerSource={row.original.trigger_source}
        pullRequestNumber={row.original.pull_request_number}
        pullRequestLink={row.original._links['pull-request']?.href}
      />
    ),
    meta: { width: '15%', className: typography.paragraph },
  }),
  columnHelper.accessor('triggered_at', {
    header: 'When',
    cell: ({ row }) => formatRelativeTime(row.original.triggered_at),
    meta: { width: '15%', className: typography.paragraph },
  }),
] as ColumnDef<RefreshRequestResponse>[]

type RefreshRequestTableProps = {
  requests: RefreshRequestResponse[]
  isLoading: boolean
  selectedRequestId?: string
  onSelectRequest: (requestId: string) => unknown
}

export const RefreshRequestTable: React.FC<RefreshRequestTableProps> = ({
  requests,
  isLoading,
  selectedRequestId,
  onSelectRequest,
}) => (
  <Table
    tableName="Update history"
    data={requests}
    columns={columns}
    getRowId={row => row.id}
    isLoading={isLoading}
    highlightedRowId={selectedRequestId}
    // Guard against interactive-descendant clicks so the drawer doesn't open when a user clicks
    // the commit or PR link. `Table` only applies this guard to keyboard activations.
    onRowClick={(row, event) => {
      if (isInteractiveElementClick(event)) {
        return
      }
      onSelectRequest(row.original.id)
    }}
    emptyStateMessage="No updates recorded yet — automatic updates happen when pushes land on the default branch."
  />
)

type CommitCellProps = {
  shortSha: string | undefined
  commitLink: string | undefined
}

const CommitCell: React.FC<CommitCellProps> = ({ shortSha, commitLink }) =>
  match({ shortSha, commitLink })
    .with({ shortSha: P.nullish }, () => <>—</>)
    // The linked sha takes the link colour; only the unlinked one keeps `shaText`'s neutral fill.
    .with({ shortSha: P.string, commitLink: P.string }, ({ shortSha: sha, commitLink: href }) => (
      <a href={href} target="_blank" rel="noopener noreferrer" className={styles.externalLink}>
        <span>{sha}</span>
        <ArrowSquareOutIcon className={styles.externalLinkIcon} size={12} weight="light" />
      </a>
    ))
    .otherwise(({ shortSha: sha }) => <span className={styles.shaText}>{sha}</span>)

type PullRequestCellProps = {
  triggerSource: RefreshTriggerSourceResponse
  pullRequestNumber: number | undefined
  pullRequestLink: string | undefined
}

const PullRequestCell: React.FC<PullRequestCellProps> = ({ triggerSource, pullRequestNumber, pullRequestLink }) => {
  if (triggerSource !== 'merged-pr' || pullRequestNumber === undefined) {
    return <>—</>
  }
  if (pullRequestLink) {
    return (
      <a href={pullRequestLink} target="_blank" rel="noopener noreferrer" className={styles.externalLink}>
        <span>#{pullRequestNumber}</span>
        <ArrowSquareOutIcon className={styles.externalLinkIcon} size={12} weight="light" />
      </a>
    )
  }
  return <span>#{pullRequestNumber}</span>
}
